import { Link, useOutletContext } from "react-router-dom";
import { isPubliclyVisible } from "@alpha/shared";
import { divisionBracket } from "../../../lib/bundle";
import { tournamentPath } from "../../../lib/routes";
import type { Bundle, Division } from "../../../lib/types";
import { PHASE_LABEL } from "../../../lib/tournamentPhases";
import { Button, ButtonLink, Card, EmptyState } from "../../../components/ui";
import { phasePath } from "./PhaseRail";
import type { TournamentContext } from "./TournamentShell";

/**
 * Where the event is and what to do next (console spec §5.1): one Next up
 * card, then a tile per division. A scorer sees the same page without status
 * controls — `requestStatus` is only ever offered when Next up names a status
 * action, and derivePhases never names one for a caller without `editEvent`.
 */
export function Overview() {
  const { tid, bundle, caps, phases, requestStatus, statusPending } =
    useOutletContext<TournamentContext>();
  const { nextUp } = phases;
  const divisions = [...bundle.divisions].sort((a, b) => a.order - b.order);
  const isPublic = isPubliclyVisible(bundle.tournament.status);

  return (
    <div className="space-y-6">
      {bundle.tournament.status === "draft" && caps.editEvent && (
        <p className="text-sm text-slate-600">Your event is a draft. It is not on the public list yet.</p>
      )}
      <Card className="flex flex-wrap items-center justify-between gap-4 p-5 sm:p-6">
        <div className="min-w-0 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-brandText">Next up</p>
          <h2 className="font-display text-lg font-semibold tracking-tight text-navy-950 sm:text-xl">
            {nextUp.message}
          </h2>
          {nextUp.action === "publish" && (
            <p className="max-w-prose text-sm text-slate-600">
              Publishing puts this event on the public directory. Teams can register once the registration window is open.
            </p>
          )}
        </div>
        {nextUp.action === "go" && nextUp.phase !== "overview" ? (
          <ButtonLink to={phasePath(tid, nextUp.phase)}>Open {PHASE_LABEL[nextUp.phase]}</ButtonLink>
        ) : nextUp.action === "publish" ? (
          <Button onClick={() => requestStatus("published")} disabled={statusPending}>
            Publish
          </Button>
        ) : nextUp.action === "complete" ? (
          <Button onClick={() => requestStatus("complete")} disabled={statusPending}>
            Mark complete
          </Button>
        ) : null}
      </Card>

      {divisions.length === 0 ? (
        <EmptyState
          title="No divisions yet"
          hint={caps.runEvent ? "Every team enters a division." : "An organizer sets the divisions up."}
          action={
            caps.runEvent ? (
              <ButtonLink variant="secondary" to={phasePath(tid, "setup")}>
                Open Setup
              </ButtonLink>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {divisions.map((d) => (
            <DivisionTile key={d.id} tid={tid} bundle={bundle} division={d} isPublic={isPublic} />
          ))}
        </div>
      )}

      {isPublic && caps.editEvent && (
        <div className="flex items-center gap-4">
          <p className="font-mono text-sm text-slate-700">{tournamentPath(tid)}</p>
          <Button type="button" variant="secondary" onClick={() => void navigator.clipboard.writeText(new URL(tournamentPath(tid), window.location.origin).toString())}>
            Copy link
          </Button>
        </div>
      )}
    </div>
  );
}

function seedingState(bundle: Bundle, d: Division, entries: number): string {
  const bracket = bundle.games.filter((g) => g.divisionId === d.id && g.type === "bracket");
  if (bracket.length > 0) {
    return divisionBracket(bundle, d.id).championId ? "Champion decided" : "Bracket created";
  }
  if (d.lockedSeeds) return "Seeds locked";
  return entries > 0 ? "Seeds provisional" : "—";
}

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * One division at a glance. The figures are the same four the rail carries,
 * scoped to this division, in a label-over-value grid rather than sentences:
 * an organizer scanning three tiles compares "12/12" to "6/9" without reading.
 */
function DivisionTile({
  tid,
  bundle,
  division: d,
  isPublic,
}: {
  tid: string;
  bundle: Bundle;
  division: Division;
  isPublic: boolean;
}) {
  const entries = bundle.entries.filter((e) => e.divisionId === d.id).length;
  const pools = bundle.pools.filter((p) => p.divisionId === d.id).length;
  const games = bundle.games.filter((g) => g.divisionId === d.id);
  const scheduled = games.filter((g) => g.scheduledAt && (g.venueId || g.field)).length;
  const finished = games.filter((g) => g.status === "final" || g.status === "forfeit").length;

  const cells: [string, string][] = [
    ["Teams", `${entries} · ${count(pools, "pool")}`],
    ["Scheduled", games.length === 0 ? "0 games" : `${scheduled} / ${games.length}`],
    ["Final", games.length === 0 ? "—" : String(finished)],
    ["Bracket", seedingState(bundle, d, entries)],
  ];

  const body = (
    <>
      <div className="font-display font-semibold text-navy-950">{d.name}</div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        {cells.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className="truncate text-sm tabular-nums text-slate-700">{value}</dd>
          </div>
        ))}
      </dl>
    </>
  );

  // The tile links to the public schedule once there is one: it is the page a
  // coach asks about, and the quickest way to see the division as they do.
  return isPublic ? (
    <Link
      to={`/t/${tid}/d/${d.id}/schedule`}
      className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 hover:bg-brand-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
    >
      {body}
    </Link>
  ) : (
    <div className="rounded-xl border border-slate-200 bg-white p-4">{body}</div>
  );
}
